import type { WebRtcStream } from '@scaleaq/sitecontrol-lib';

import { Tooltip, TrafficLight } from '@scaleaq/scaleui';

import { Z_INDEX } from '@/constants';
import { useWebRtcStatsStore } from '@/stores';
import { getCameraStreamHealth } from '@/utils/streamHealth.functions.ts';

import { CameraStreamHealthTooltip } from './cameraStreamWebRtcStatus/CameraStreamHealthTooltip.tsx';
import { useLiveStreamStatus } from './cameraStreamWebRtcStatus/useLiveStreamStatus.ts';

type CameraStreamWebRtcStatusProps = {
  streamUri: string | undefined;
  state: RTCPeerConnectionState;
  isVideoPaused?: boolean;
  /** HLS rewind is playing; only the source leg is measured then. */
  isRewinding?: boolean;
  cameraId?: string;
  /** Stream whose source health applies: the selected tier, or HIGH while rewinding. */
  streamName?: string;
  /** Streams from the REST camera list, until the first `CameraChanged` push. */
  webRtcStreams?: WebRtcStream[];
  /**
   * Render the tooltip in a portal, above the camera switcher and other overlays. Off in
   * fullscreen, where a portal to the document body would not be visible.
   */
  usePortal?: boolean;
};

/**
 * Subscribes to WebRTC client stats and `CameraChanged` pushes only here, so the parent widget
 * (and video) do not re-render on each poll or push.
 */
export const CameraStreamWebRtcStatus = ({
  streamUri,
  state,
  isVideoPaused = false,
  isRewinding = false,
  cameraId,
  streamName,
  webRtcStreams,
  usePortal = true,
}: CameraStreamWebRtcStatusProps) => {
  const webRtcStats = useWebRtcStatsStore(s => (streamUri ? s.stats[streamUri] : undefined));
  const streamStatus = useLiveStreamStatus({
    cameraId,
    streamName,
    fallbackWebRtcStreams: webRtcStreams,
  });

  const { trafficLight, cause } = getCameraStreamHealth({
    connectionState: state,
    isVideoPaused,
    isRewinding,
    clientStats: webRtcStats,
    streamStatus,
  });

  return (
    <Tooltip
      usePortal={usePortal}
      zIndex={Z_INDEX.content.floatingTooltip}
      placement='bottom-end'
      tooltip={
        <CameraStreamHealthTooltip
          state={state}
          isVideoPaused={isVideoPaused}
          isRewinding={isRewinding}
          cause={cause}
          clientStats={webRtcStats}
          streamStatus={streamStatus}
        />
      }
    >
      <TrafficLight type={trafficLight} />
    </Tooltip>
  );
};
